import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { MenuItem, TextField } from "@mui/material";
import { withSuperAdminAuth, authData } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { CountryList } from "../../../components/Constants";
import billingStyles from "../../../components/billing.module.css";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";
import SharedPasswordFields, { adminAccountsLookup } from "../../../components/SharedPasswordFields";

const COUNTRIES = [{ value: "GB", label: "United Kingdom" }, ...CountryList.filter((c) => c.value !== "GB")];

// Create a company (starts on the free trial) or edit its profile
const CompanyForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [toast, showMessage, closeToast] = useToast();
  const API_TOKEN = authData()?.api_token;

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: { company_name: "", domain: "", name: "", email: "", phone: "", company_address: "", pickup_country: "GB", delivery_country: "GH", vat_number: "", reg_number: "" },
  });

  const { websiteData } = useWebsite();
  useSiteBranding(isEdit ? "Edit company" : "Add company", websiteData);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(process.env.REACT_APP_API_URL + "v1/companies/" + id, { headers: { Authorization: `Bearer ${API_TOKEN}` } }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        const c = response.data.data;
        reset({ company_name: c.company_name || "", domain: c.domain || "", name: c.name || "", email: c.email || "", phone: c.phone || "", password: "", company_address: c.company_address || "", pickup_country: c.pickup_country || "GB", delivery_country: c.delivery_country || "GH", vat_number: c.vat_number || "", reg_number: c.reg_number || "" });
      }
    }).catch(() => setLoading(false));
  }, [id, isEdit, API_TOKEN, reset]);

  const onSubmit = (data) => {
    setSaving(true);
    const payload = { ...data };
    if (isEdit) {
      delete payload.password;
      delete payload.conf_password;
      delete payload.password_mode;
    }
    const url = process.env.REACT_APP_API_URL + "v1/companies" + (isEdit ? "/" + id : "");
    axios.post(url, payload, { headers: { Authorization: `Bearer ${API_TOKEN}` } }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        const goToCompany = () => navigate(`/super-admin/companies/${response.data.data?.id || id}`);
        // The admin's email already had accounts: say what happened to their password first
        if (response.data.password_note) {
          showMessage(response.data.message, "info");
          setTimeout(goToCompany, 3000);
        } else {
          goToCompany();
        }
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch((error) => {
      setSaving(false);
      showMessage(error?.response?.data?.message || "Could not save the company.", "error");
    });
  };

  const field = (name, label, rules = {}, props = {}) => (<Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field: f }) => (<TextField {...f} {...props} label={label} fullWidth size="small" error={!!errors[name]} helperText={errors[name]?.message || props.helperText} />)}
  />);

  return (<div className={`${styles.Page}`}>
    <PageHeader title={isEdit ? "Edit Company" : "Add Company"} trail={[{ label: "Companies", to: "/super-admin/companies" }]} />

    {loading && <TableSkeleton rows={4} />}

    {!loading && (<form onSubmit={handleSubmit(onSubmit)}>
      <div className={`${styles.Panel}`} style={{ marginBottom: 18 }}>
        <p className={`${styles.PanelTitle}`} style={{ marginBottom: 16 }}>Company</p>
        <div className={`${styles.FormGrid}`}>
          {field("company_name", "Company name", { required: "Company name is required" })}
          {field("domain", "Domain", {}, { placeholder: "acme.eflogistics.io", helperText: "Host the company's site and dashboard run on" })}
          <div className={`${styles.FormFull}`}>{field("company_address", "Address")}</div>
          {field("pickup_country", "Pickup country", {}, { select: true, children: COUNTRIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>) })}
          {field("delivery_country", "Delivery country", {}, { select: true, children: COUNTRIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>) })}
          {field("reg_number", "Registration no.")}
          {field("vat_number", "VAT number")}
        </div>
      </div>

      <div className={`${styles.Panel}`}>
        <p className={`${styles.PanelTitle}`} style={{ marginBottom: 16 }}>Owner {isEdit ? "" : "/ admin login"}</p>
        <div className={`${styles.FormGrid}`}>
          {field("name", "Owner name", { required: "Owner name is required" })}
          {field("email", "Email", { required: "Email is required", pattern: { value: /^\S+@\S+\.\S+$/, message: "Enter a valid email" } })}
          {field("phone", "Phone", { required: "Phone is required" })}
          {!isEdit && (<SharedPasswordFields control={control} watch={watch} errors={errors} lookupAccounts={adminAccountsLookup(API_TOKEN)} fullGroupClassName={styles.FormFull} />)}
        </div>
        {!isEdit && <p className={`${styles.Muted}`} style={{ marginTop: 14 }}>The company starts on the Basic plan with the free trial. Its first invoice is emailed automatically one month after the trial ends.</p>}
      </div>

      <div className={`${styles.FormActions}`}>
        <button type="button" className={`${billingStyles.SecondaryBU}`} onClick={() => navigate(-1)} disabled={saving}>Cancel</button>
        <button type="submit" className={`${billingStyles.PrimaryBU}`} disabled={saving}>{saving ? "Saving…" : isEdit ? "Save changes" : "Create company"}</button>
      </div>
    </form>)}

    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(CompanyForm);
